/**
 * *(Owner, 2026-10-16)* The sign-in trace — what one phone did between "sign in" and the screen it shows.
 *
 * One student's phone (iPhone, home-screen web app) returns to the sign-in page after the Google popup,
 * while the server records the same account as signed in. The server cannot see the steps in between,
 * so this module records them on the device and sends them through `client.report`:
 *
 *  - the page load (navigation type, installed-app mode, path, referrer origin),
 *  - the sign-in click and the popup's outcome with its Firebase error code,
 *  - every session status change (`in` / `out` / `loading`) and every access answer,
 *  - an explicit sign-out, visibility changes, `pagehide` / `pageshow`, and the page's own errors.
 *
 * The rules
 * ---------
 *  - **It survives the page.** Steps are kept in `localStorage` (the newest `MAX_TRACE_STEPS`), because a
 *    session that ends signed out cannot send: `client.report` needs an Account. They leave on the next
 *    signed-in moment of the same browser storage, and are removed only after the server accepted them.
 *  - **It sends only what matters.** A plain page load of a signed-in student is not sent. A trace is sent
 *    once it holds a sign-in attempt, a popup outcome, an access error or a session that went out without
 *    a sign-out.
 *  - **It carries no personal data.** Step codes, status words, Firebase error codes and paths only; no
 *    e-mail, uid or token. The server scrubs the text again.
 *  - **It never throws.** Every capability is a port and every call is guarded.
 */

/** At most this many steps are kept on the device. */
export const MAX_TRACE_STEPS = 54;

/** Steps per reported event: one detail key each, under the contract's twenty. */
export const STEPS_PER_EVENT = 18;

/** A notable trace leaves this long after its latest step while the session is signed in. */
export const TRACE_FLUSH_DELAY_MS = 2_000;

export const TRACE_STORAGE_KEY = 'simple-qa.signin-trace.v1';

const MAX_STEP_LENGTH = 200;

/** The steps that make a trace worth sending. */
const NOTABLE_CODES: ReadonlySet<string> = new Set(['click', 'popup_ok', 'popup_err', 'access_error']);
const NOTABLE_LINE = /^\S+ \S+ (?:click|popup_ok|popup_err|access_error|session out unexpected)(?: |$)/;

export type TraceSession = 'in' | 'out' | 'loading';

export interface TraceEvent {
  readonly message: string;
  readonly details: Readonly<Record<string, string>>;
}

export interface SignInTracePorts {
  readonly read: () => string | null;
  readonly write: (value: string) => void;
  readonly now: () => Date;
  /** Sends one report; resolves when the server accepted it. */
  readonly send: (events: readonly TraceEvent[]) => Promise<unknown>;
  /** This page load's short id, so the steps of different loads can be told apart. */
  readonly pageId: string;
}

interface Stored {
  readonly steps: string[];
  readonly notable: boolean;
}

export interface SignInTrace {
  readonly step: (code: string, extra?: string) => void;
  readonly session: (status: TraceSession) => void;
  readonly flush: () => void;
}

function clock(date: Date): string {
  // `HH:MM:SS.d` UTC: colons keep the server's long-digit scrubber away from the time.
  return date.toISOString().slice(11, 21);
}

export function createSignInTrace(ports: SignInTracePorts): SignInTrace {
  let signedIn = false;
  let signedInThisPage = false;
  let signedOutByUser = false;
  let sending = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function load(): Stored {
    try {
      const raw = ports.read();
      const parsed: unknown = raw === null ? null : JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as Stored).steps)) {
        const steps = (parsed as Stored).steps.filter((s): s is string => typeof s === 'string');
        return { steps: steps.slice(-MAX_TRACE_STEPS), notable: (parsed as Stored).notable === true };
      }
    } catch {
      // A broken or foreign value starts a fresh trace.
    }
    return { steps: [], notable: false };
  }

  function save(value: Stored): void {
    try {
      ports.write(JSON.stringify(value));
    } catch {
      // Storage full or blocked: the trace simply lives no longer than the page.
    }
  }

  function schedule(): void {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(flush, TRACE_FLUSH_DELAY_MS);
  }

  function record(code: string, extra: string | undefined, notable: boolean): void {
    try {
      const line = `${ports.pageId} ${clock(ports.now())} ${code}${extra ? ` ${extra}` : ''}`.slice(0, MAX_STEP_LENGTH);
      const stored = load();
      save({ steps: [...stored.steps, line].slice(-MAX_TRACE_STEPS), notable: stored.notable || notable });
      if (signedIn && (stored.notable || notable)) schedule();
    } catch {
      // Tracing is best effort.
    }
  }

  function step(code: string, extra?: string): void {
    if (code === 'signout') signedOutByUser = true;
    record(code, extra, NOTABLE_CODES.has(code));
  }

  function session(status: TraceSession): void {
    // A session that leaves on its own after being in on this page is the failure being traced.
    const unexpectedOut = status === 'out' && signedInThisPage && !signedOutByUser;
    if (status === 'in') {
      signedInThisPage = true;
      signedOutByUser = false;
    }
    signedIn = status === 'in';
    record('session', unexpectedOut ? 'out unexpected' : status, unexpectedOut);
  }

  function flush(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (!signedIn || sending) return;
    const stored = load();
    if (!stored.notable || stored.steps.length === 0) return;
    const sent = stored.steps;
    const events: TraceEvent[] = [];
    for (let i = 0; i < sent.length; i += STEPS_PER_EVENT) {
      const details: Record<string, string> = {};
      sent.slice(i, i + STEPS_PER_EVENT).forEach((line, j) => {
        details[`s${String(i + j).padStart(2, '0')}`] = line;
      });
      events.push({
        message: `sign-in trace ${ports.pageId} ${i / STEPS_PER_EVENT + 1}/${Math.ceil(sent.length / STEPS_PER_EVENT)}`,
        details,
      });
    }
    sending = true;
    let pending: Promise<unknown>;
    try {
      pending = ports.send(events);
    } catch {
      sending = false;
      return;
    }
    pending.then(
      () => {
        sending = false;
        // Steps recorded while the report was in flight stay, and are notable only if they say so.
        const now = load();
        const last = now.steps.lastIndexOf(sent[sent.length - 1] ?? '');
        const rest = last >= 0 ? now.steps.slice(last + 1) : now.steps;
        const notable = rest.some((line) => NOTABLE_LINE.test(line));
        save({ steps: rest, notable });
        if (notable) schedule();
      },
      () => {
        sending = false;
      },
    );
  }

  return Object.freeze({ step, session, flush });
}

/* ------------------------------------------------------------------ *
 * The page's trace
 * ------------------------------------------------------------------ */

let installed: SignInTrace | null = null;

/** Record one step of the page's sign-in trace. A no-op until installed; never throws. */
export function traceSignInStep(code: string, extra?: string): void {
  installed?.step(code, extra);
}

/** Record the session's status as the page now sees it. */
export function traceSession(status: TraceSession): void {
  installed?.session(status);
}

function navigationType(): string {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return entry?.type ?? '';
  } catch {
    return '';
  }
}

function referrerOrigin(): string {
  try {
    return document.referrer ? new URL(document.referrer).origin : '';
  } catch {
    return '';
  }
}

/**
 * Installs the page's trace with its sender, and records the page load. Called once by the client
 * reporter's install; a second call is a no-op.
 */
export function installSignInTrace(send: SignInTracePorts['send'], standalone: () => boolean): void {
  if (installed !== null) return;
  try {
    const pageId = Math.random().toString(36).slice(2, 7);
    const trace = createSignInTrace({
      read: () => window.localStorage.getItem(TRACE_STORAGE_KEY),
      write: (value) => window.localStorage.setItem(TRACE_STORAGE_KEY, value),
      now: () => new Date(),
      send,
      pageId,
    });
    installed = trace;
    trace.step(
      'load',
      `nav=${navigationType()} sa=${standalone()} path=${window.location.pathname} ref=${referrerOrigin()} vis=${document.visibilityState} idb=${typeof indexedDB !== 'undefined'}`,
    );
    document.addEventListener('visibilitychange', () => trace.step('vis', document.visibilityState));
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) trace.step('pageshow', 'persisted');
    });
    window.addEventListener('pagehide', (event) => {
      trace.step('pagehide', `persisted=${event.persisted} path=${window.location.pathname}`);
      trace.flush();
    });
    window.addEventListener('error', (event) => trace.step('err', `${event.message}`.slice(0, 100)));
  } catch {
    installed = null;
  }
}
